import { Suspense, lazy, useMemo, useState } from 'react';
import { ApiRequestError } from './api.js';
import { GuessInput } from './components/GuessInput.js';
import type { ChampionItem } from './components/GuessInput.js';
import { Board } from './components/Board.js';
import { ReplayPage } from './components/ReplayPage.js';
import { ResultPanel } from './components/ResultPanel.js';
import { AccountPage } from './components/AccountPage.js';
import { StatsPage } from './components/StatsPage.js';
import { HowToPlay } from './components/HowToPlay.js';
import { SettingsPage } from './components/SettingsPage.js';
import { useSettings } from './settings.js';
import { AuthForm } from './components/AuthForm.js';
import { useChampions, useCurrentGame, useGameActions, useMe } from './queries.js';
import { isAdminView, useView } from './route.js';

const AdminPage = lazy(() => import('./admin/AdminPage.js'));

const messageOf = (err: unknown): string | null =>
  err instanceof ApiRequestError ? err.message : err ? 'Something went wrong.' : null;

export function App() {
  const champions = useChampions();
  const current = useCurrentGame();
  const { guess, start, giveUp } = useGameActions();
  const me = useMe();
  const [view, go] = useView();
  const [settings, updateSettings] = useSettings();
  const [confirmGiveUp, setConfirmGiveUp] = useState(false);

  const list = champions.data?.champions;
  const byId = useMemo(
    () => new Map<string, ChampionItem>((list ?? []).map((c) => [c.id, c])),
    [list],
  );
  const game = current.data ?? null;
  const guessedIds = useMemo(() => new Set(game?.guesses.map((g) => g.championId) ?? []), [game]);
  const over = game !== null && game.status !== 'in_progress';
  const answer = over ? byId.get(game.answer?.championId ?? '') : undefined;
  const user = me.data ?? null;
  const failed = champions.isError || current.isError;

  return (
    <div className="shell">
      <header className="masthead">
        <div>
          <h1>
            <a href="#" onClick={() => go('play')}>
              LoLdle Multi
            </a>
          </h1>
          <p>Guess the champion. Every guess shows how close you are.</p>
        </div>
        <nav className="account-nav" aria-label="Account">
          <button type="button" className="link" onClick={() => go('how-to-play')}>
            How to play
          </button>
          <button type="button" className="link" onClick={() => go('stats')}>
            Stats
          </button>
          <button type="button" className="link" onClick={() => go('settings')}>
            Settings
          </button>
          {user?.role === 'admin' ? (
            <button type="button" className="link" onClick={() => go('admin')}>
              Admin
            </button>
          ) : null}
          {user && !user.isGuest ? (
            <button type="button" onClick={() => go('account')}>
              {user.username}
            </button>
          ) : (
            <>
              <button type="button" className="link" onClick={() => go('login')}>
                Log in
              </button>
              <button type="button" onClick={() => go('register')}>
                Create account
              </button>
            </>
          )}
        </nav>
      </header>

      <main>
        {isAdminView(view) ? (
          user?.role === 'admin' ? (
            <Suspense
              fallback={
                <p className="muted" role="status">
                  Loading…
                </p>
              }
            >
              <AdminPage view={view} go={go} />
            </Suspense>
          ) : me.isPending ? (
            <p className="muted" role="status">
              Loading…
            </p>
          ) : (
            <div className="notice" role="alert">
              <p>The admin area is for administrators. Log in with an admin account.</p>
              <button type="button" onClick={() => go('login')}>
                Log in
              </button>
            </div>
          )
        ) : view === 'how-to-play' ? (
          <HowToPlay go={go} />
        ) : view === 'settings' ? (
          <SettingsPage settings={settings} onChange={updateSettings} go={go} />
        ) : view === 'stats' ? (
          <StatsPage byId={byId} go={go} />
        ) : view.startsWith('game/') ? (
          <ReplayPage gameId={view.slice(5)} byId={byId} go={go} />
        ) : view === 'login' || view === 'register' ? (
          <AuthForm mode={view} hasGuestGames={(game?.guesses.length ?? 0) > 0} go={go} />
        ) : view === 'account' ? (
          user && !user.isGuest ? (
            <AccountPage user={user} go={go} />
          ) : me.isPending ? (
            <p className="muted" role="status">
              Loading…
            </p>
          ) : (
            <AuthForm mode="login" hasGuestGames={false} go={go} />
          )
        ) : failed ? (
          <div className="notice" role="alert">
            <p>Could not load the game. Check your connection and try again.</p>
            <button
              type="button"
              onClick={() => {
                void champions.refetch();
                void current.refetch();
              }}
            >
              Retry
            </button>
          </div>
        ) : !list || current.isPending ? (
          <p className="muted" role="status">
            Loading…
          </p>
        ) : over ? (
          <ResultPanel
            game={game}
            answer={answer}
            busy={start.isPending}
            onPlayAgain={() => start.mutate()}
          />
        ) : (
          <GuessInput
            champions={list}
            guessedIds={guessedIds}
            busy={guess.isPending}
            error={messageOf(guess.error)}
            onSubmit={(id) => guess.mutate(id)}
          />
        )}

        {view === 'play' && game ? (
          <Board key={game.id} guesses={game.guesses} byId={byId} />
        ) : null}

        {view === 'play' && game && !over ? (
          <div className="actions">
            {confirmGiveUp ? (
              <>
                <span>Reveal the answer and end this game?</span>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmGiveUp(false);
                    giveUp.mutate();
                  }}
                >
                  Give up
                </button>
                <button type="button" onClick={() => setConfirmGiveUp(false)}>
                  Keep playing
                </button>
              </>
            ) : (
              <button type="button" className="link" onClick={() => setConfirmGiveUp(true)}>
                Give up
              </button>
            )}
          </div>
        ) : null}
      </main>

      <footer className="footer">
        <p>
          LoLdle Multi isn&rsquo;t endorsed by Riot Games and doesn&rsquo;t reflect the views or
          opinions of Riot Games or anyone officially involved in producing or managing Riot Games
          properties. Riot Games, and all associated properties are trademarks or registered
          trademarks of Riot Games, Inc.
        </p>
        <nav aria-label="Footer">
          <button type="button" className="link" onClick={() => go('how-to-play')}>
            How to play
          </button>
          <button type="button" className="link" onClick={() => go('settings')}>
            Settings
          </button>
        </nav>
      </footer>
    </div>
  );
}
